import React, { FC, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  CompanyType,
  ApplicationFeature,
  UpdateCompanyDto,
  UsersListItemDto,
  CreateAdminCompanyDto,
} from "@green0meter/api-client";
import { useTranslation } from "react-i18next";

import {
  CompanyEditorProps,
  CompanyEditorFormData,
  FeatureWithUsers,
  FeatureOption,
} from "./CompanyEditor.types";
import { defaultFormValues } from "./CompanyEditor.constants";
import CompanyName from "./components/CompanyName";
import CompanyVat from "./components/CompanyVat";
import Country from "./components/Country";
import CompanyTypeField from "./components/CompanyTypeField";
import AccountTypeField from "./components/AccountTypeField";
import NaceClassification from "./components/NaceClassification";
import OwnershipShare from "./components/OwnershipShare";
import PremiumAccess from "./components/PremiumAccess";
import OwnerDetails from "./components/OwnerDetails";
import ApplicationFeatures from "./components/ApplicationFeatures";
import FeatureUserRestrictions from "./components/FeatureUserRestrictions";

import { Drawer } from "@components/common";
import { Button, Stack } from "@components/base";
import { useUserStore } from "@store/user";
import {
  useUpdateAdminCompany,
  useCreateAdminCompany,
  useGetApplicationFeatureFunctionalities,
  useSetCompanyFeatureFunctionalities,
  useGetUsers,
} from "@api";

const CompanyEditor: FC<CompanyEditorProps> = ({
  company,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { user } = useUserStore();

  const isCreateMode = !company;

  const { updateAdminCompanyAsync, isPendingUpdateAdminCompany } =
    useUpdateAdminCompany(user);
  const { createAdminCompanyAsync, isPendingCreateAdminCompany } =
    useCreateAdminCompany(user);
  const { applicationFeatureFunctionalities } =
    useGetApplicationFeatureFunctionalities(user);
  const { setCompanyFeatureFunctionalitiesAsync } =
    useSetCompanyFeatureFunctionalities(user);
  const { users } = useGetUsers(user, { companyId: company?.id, pageSize: 100 });

  const [selectedFeatures, setSelectedFeatures] = useState<FeatureWithUsers[]>([]);

  const usersList = useMemo(() => users?.items ?? [], [users]);

  const { control, handleSubmit, reset, watch, setValue } =
    useForm<CompanyEditorFormData>({
      defaultValues: defaultFormValues,
    });

  const watchedCompanyType = watch("companyType");
  const isMasterCompany = watchedCompanyType === CompanyType.Master;

  useEffect(() => {
    if (isOpen) {
      if (company) {
        reset({
          ...defaultFormValues,
          name: company.name ?? "",
          vat: company.companyId ?? "",
          country: company.country ?? "",
          companyType: company.companyType,
          accountType: company.accountType ?? defaultFormValues.accountType,
          naceSectionId: company.naceSectionId ?? "",
          naceDivisionId: company.naceDivisionId ?? null,
          ownershipSharePercentage: company.ownershipShare ?? null,
          isPremiumActive: company.isPremiumActive ?? false,
        });

        if (company.companyFeatureFunctionalities) {
          setSelectedFeatures(
            company.companyFeatureFunctionalities.map((f) => ({
              applicationFeatureFunctionalityId:
                f.applicationFeatureFunctionalityId,
              allowedUserIds: f.allowedUserIds ?? [],
            })),
          );
        } else {
          setSelectedFeatures([]);
        }
      } else {
        reset(defaultFormValues);
        setSelectedFeatures([]);
      }
    }
  }, [company, isOpen, reset]);

  const availableFeatures = useMemo((): FeatureOption[] => {
    return (
      applicationFeatureFunctionalities?.map((f) => ({
        id: f.id,
        label:
          ApplicationFeature[f.applicationFeature as number] ??
          `Feature ${f.id}`,
        applicationFeature: f.applicationFeature,
      })) ?? []
    );
  }, [applicationFeatureFunctionalities]);

  const handleFeatureChange = (
    _event: React.SyntheticEvent,
    newValue: FeatureOption[],
  ): void => {
    const newFeatures = newValue.map((v) => {
      const existing = selectedFeatures.find(
        (f) => f.applicationFeatureFunctionalityId === v.id,
      );

      return {
        applicationFeatureFunctionalityId: v.id,
        allowedUserIds: existing?.allowedUserIds ?? [],
      };
    });

    setSelectedFeatures(newFeatures);
  };

  const handleUserIdsChange = (
    featureId: string,
    selectedUsers: UsersListItemDto[],
  ): void => {
    setSelectedFeatures((prev) =>
      prev.map((f) =>
        f.applicationFeatureFunctionalityId === featureId
          ? { ...f, allowedUserIds: selectedUsers.map((u) => u.id) }
          : f,
      ),
    );
  };

  const onSubmit = async (formData: CompanyEditorFormData): Promise<void> => {
    try {
      if (isCreateMode) {
        const dto: CreateAdminCompanyDto = {
          name: formData.name,
          vat: formData.vat,
          country: formData.country || null,
          accountType: formData.accountType,
          naceDivisionId: formData.naceDivisionId,
          featureFunctionalityIds: selectedFeatures.map(
            (f) => f.applicationFeatureFunctionalityId,
          ),
          ownerEmail: formData.ownerEmail,
          ownerFirstName: formData.ownerFirstName,
          ownerLastName: formData.ownerLastName,
          ownerPassword: formData.ownerPassword,
          ownerPhoneNumber: formData.ownerPhoneNumber || null,
          isPremiumActive: formData.isPremiumActive,
        };

        await createAdminCompanyAsync(dto);
      } else {
        const dto: UpdateCompanyDto = {
          name: formData.name,
          vat: formData.vat,
          country: formData.country || null,
          companyType: formData.companyType,
          accountType: formData.accountType,
          naceDivisionId: formData.naceDivisionId,
          ownershipSharePercentage: formData.ownershipSharePercentage,
          isPremiumActive: formData.isPremiumActive,
        };

        await updateAdminCompanyAsync({
          companyId: company.id,
          data: dto,
        });

        await setCompanyFeatureFunctionalitiesAsync({
          companyId: company.id,
          data: {
            featureFunctionalities: selectedFeatures.map((f) => ({
              applicationFeatureFunctionalityId:
                f.applicationFeatureFunctionalityId,
              allowedUserIds: f.allowedUserIds,
            })),
          },
        });
      }

      handleClose();
      onSuccess();
    } catch (error) {
      console.error("Failed to save company:", error);
    }
  };

  const handleClose = (): void => {
    reset();
    setSelectedFeatures([]);
    onClose();
  };

  const selectedFeatureIds = selectedFeatures.map(
    (f) => f.applicationFeatureFunctionalityId,
  );
  const selectedFeatureOptions = availableFeatures.filter((f) =>
    selectedFeatureIds.includes(f.id),
  );

  const isLoading = isPendingUpdateAdminCompany || isPendingCreateAdminCompany;

  return (
    <Drawer
      open={isOpen}
      onClose={handleClose}
      title={isCreateMode ? t("admin.createCompany") : t("admin.editCompany")}
      content={
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{ display: "flex", flexDirection: "column", gap: 16 }}
        >
          <Stack spacing={2}>
            <CompanyName control={control} isCreateMode={isCreateMode} />
            <CompanyVat control={control} isCreateMode={isCreateMode} />
            <Country control={control} />

            {!isCreateMode && <CompanyTypeField control={control} />}

            {(isCreateMode || isMasterCompany) && (
              <AccountTypeField
                control={control}
                isCreateMode={isCreateMode}
              />
            )}

            <NaceClassification control={control} setValue={setValue} />

            {!isCreateMode && !isMasterCompany && (
              <OwnershipShare control={control} />
            )}

            {isMasterCompany && <PremiumAccess control={control} />}

            {isCreateMode && <OwnerDetails control={control} />}

            <ApplicationFeatures
              availableFeatures={availableFeatures}
              selectedFeatureOptions={selectedFeatureOptions}
              onChange={handleFeatureChange}
            />

            {!isCreateMode && selectedFeatures.length > 0 && (
              <FeatureUserRestrictions
                selectedFeatures={selectedFeatures}
                availableFeatures={availableFeatures}
                usersList={usersList}
                onUserIdsChange={handleUserIdsChange}
              />
            )}

            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              sx={{ mt: 2 }}
            >
              <Button variant="outlined" onClick={handleClose}>
                {t("common.cancel")}
              </Button>
              <Button variant="contained" type="submit" loading={isLoading}>
                {isCreateMode ? t("common.create") : t("common.save")}
              </Button>
            </Stack>
          </Stack>
        </form>
      }
    />
  );
};

export default CompanyEditor;
